/**
 * EarthSphere Component
 * Renders the Earth sphere with UV-based tiled shader material
 */

import React, { useEffect, useRef, memo, useMemo, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { DEFAULT_SPHERE_SEGMENTS } from './utilities/earthConstants'
import lodConfig from './config/lodConfig.json'

export function EarthSphere({
  radius = 2.5,
  segments = DEFAULT_SPHERE_SEGMENTS,
  quality = 'LOD_3',
  rotation = [0, 0, 0],
  position = [0, 0, 0],
  materialCache = {},
  sunPositionRef = null,
}) {
  const { gl } = useThree()
  const isWebGPU = gl.isWebGPURenderer || false
  const depthMeshRef = useRef()

  // Get materials from cache
  const material = materialCache[quality]?.earth || null
  const cloudsMaterial = materialCache[quality]?.clouds || null

  // Create geometries once using useMemo
  const earthGeometry = useMemo(() => {
    const geom = new THREE.SphereGeometry(radius, segments, segments)
    geom.computeTangents()
    return geom
  }, [radius, segments])

  const cloudsGeometry = useMemo(() => {
    return new THREE.SphereGeometry(radius * 1.01, segments, segments)
  }, [radius, segments])

  // Slightly smaller depth sphere to prevent z-fighting with Earth surface
  const depthGeometry = useMemo(() => {
    return new THREE.SphereGeometry(radius * 0.999, segments, segments)
  }, [radius, segments])

  // Cleanup geometries on unmount
  useEffect(() => {
    return () => {
      earthGeometry.dispose()
      cloudsGeometry.dispose()
      depthGeometry.dispose()
    }
  }, [earthGeometry, cloudsGeometry, depthGeometry])
  
  // Update light position uniforms and camera-distance fade every frame
  useFrame((state) => {
    const sp = sunPositionRef?.current
    if (material && material.userData.lightPositionUniformNode) {
      const uniformNode = material.userData.lightPositionUniformNode
      if (uniformNode.value && sp) {
        uniformNode.value.set(sp[0], sp[1], sp[2])
      }
    }
    if (cloudsMaterial && cloudsMaterial.userData.lightPositionUniformNode) {
      const uniformNode = cloudsMaterial.userData.lightPositionUniformNode
      if (uniformNode.value && sp) {
        uniformNode.value.set(sp[0], sp[1], sp[2])
      }
    }

    // Fade Earth out as camera approaches — fully visible at 3.0+, invisible at 1.8
    const camDist = state.camera.position.length()
    const fadeStart = 3.0
    const fadeEnd = 1.8
    const opacity = Math.max(0, Math.min(1, (camDist - fadeEnd) / (fadeStart - fadeEnd)))

    if (material?.userData?.opacityFadeUniform) {
      material.userData.opacityFadeUniform.value = opacity
    }
    if (cloudsMaterial?.userData?.opacityFadeUniform) {
      cloudsMaterial.userData.opacityFadeUniform.value = opacity
    }

    // When Earth is fading: disable depthWrite on Earth/clouds so starfield shows through.
    // The depth sphere handles occlusion when fully opaque.
    const isFullyOpaque = opacity >= 0.99
    if (depthMeshRef.current) {
      depthMeshRef.current.visible = isFullyOpaque
    }
    if (material) {
      material.depthWrite = isFullyOpaque
    }
    if (cloudsMaterial) {
      // clouds already has depthWrite=false by design, keep it that way
    }
  })

  // Don't render until materials are ready (prevents black flicker on first load)
  if (!material || !cloudsMaterial) {
    return null
  }

  return (
    <>
      {/* Invisible depth-write sphere — occludes trajectory line and other objects behind Earth.
          Rendered before the Earth/clouds so it writes depth even when Earth is transparent.
          Hidden when Earth is fading so starfield shows through.
          Slightly smaller (99.9%) to prevent z-fighting with Earth surface. */}
      <mesh
        ref={depthMeshRef}
        geometry={depthGeometry}
        rotation={rotation}
        position={position}
        renderOrder={-1}
      >
        <meshBasicMaterial
          colorWrite={false}
          depthWrite={true}
          side={THREE.FrontSide}
        />
      </mesh>

      {/* Earth sphere — casts shadows but does NOT receive them */}
      <mesh
        geometry={earthGeometry}
        rotation={rotation}
        position={position}
        castShadow
      >
        <primitive object={material} attach="material" />
      </mesh>

      {/* Clouds sphere - slightly larger — casts but does not receive shadows */}
      <mesh
        geometry={cloudsGeometry}
        rotation={rotation}
        position={position}
        castShadow
      >
        <primitive object={cloudsMaterial} attach="material" />
      </mesh>
    </>
  )
}

export default memo(EarthSphere)
